import { useEffect, useState } from "react";
import { Copy, CheckCircle2, ExternalLink, Link as LinkIcon, Repeat, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

/**
 * Two customer-facing signup URLs per partner:
 *  - transfer → `/signup/{code}/transfer` — for customers porting from
 *    another provider onto the partner's mirrored/wholesale pricing.
 *  - new      → `/signup/{code}/new`      — for fresh customers on the
 *    partner's retail pricing.
 *
 * Also renders a "General" URL (`/signup/{code}`) that shows every plan
 * regardless of flow, useful for uncertain leads.
 *
 * ``transferUpstream`` — the upstream RSP this partner ports customers
 * FROM (e.g. "Valve" for Macktronix). Controlled by admins on
 * Admin → Partners → Edit → "Transfer upstream provider". Falls back to
 * a "to {partner}" label when unset.
 */
export default function PartnerSignupLinkCard({ partnerCode, partnerName, transferUpstream }) {
  // Collapsed by default so the board comes into view faster on load.
  // Persist per partner so admins who keep it open on a specific board
  // don't get re-collapsed on every visit. Hooks live above the
  // ``!partnerCode`` early-return so React's hook order stays stable.
  const storageKey = `signup-links-expanded:${partnerCode || ""}`;
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === "undefined" || !partnerCode) return false;
    return window.localStorage.getItem(storageKey) === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined" || !partnerCode) return;
    window.localStorage.setItem(storageKey, expanded ? "1" : "0");
  }, [storageKey, expanded, partnerCode]);

  if (!partnerCode) return null;
  const origin = (process.env.REACT_APP_PUBLIC_ORIGIN
    || process.env.REACT_APP_BACKEND_URL
    || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const base = `${origin}/signup/${encodeURIComponent(partnerCode)}`;
  const upstream = (transferUpstream || "").trim();
  const transferLabel = upstream
    ? `Transfers from ${upstream} only!`
    : `Transfers to ${partnerName || partnerCode} only!`;
  const urls = [
    {
      key: "transfer",
      url: `${base}/transfer`,
      label: transferLabel,
      icon: Repeat,
      hint: upstream
        ? `Existing ${upstream} customer moving over — sees ${partnerName || partnerCode}'s mirrored transfer pricing.`
        : `Existing internet customer moving over — sees ${partnerName || partnerCode}'s mirrored transfer pricing.`,
    },
    {
      key: "new",
      url: `${base}/new`,
      label: "New Connections",
      icon: Sparkles,
      hint: "Fresh signup, no existing service to port — sees standard NBN retail pricing.",
    },
  ];

  return (
    <div
      data-testid="partner-signup-link-card"
      className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-slate-900 to-slate-950"
    >
      <div className="absolute inset-0 grid-bg opacity-[0.08] pointer-events-none" />
      <div className="relative">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="partner-signup-link-toggle"
          aria-expanded={expanded}
          className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-900/40 transition-colors"
        >
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold">
              <LinkIcon className="w-3 h-3" /> Your customer signup links
            </div>
            <div className="text-sm text-slate-300 mt-1 truncate">
              {expanded
                ? `Share these with customers signing up under ${partnerName || partnerCode}`
                : `Two links for ${partnerName || partnerCode} — transfers and new connections. Tap to expand.`}
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-300">
            {expanded ? "Hide" : "Show"}
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        </button>
        {expanded && (
          <div className="px-6 pb-6 pt-0" data-testid="partner-signup-link-body">
            <p className="text-xs text-slate-400 max-w-2xl">
              Every completed signup is auto-attributed to <span className="font-mono text-slate-300">{partnerCode}</span> for commissions.
              Use the right link for the right customer so they see the plans priced for them.
            </p>
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-3">
              {urls.map((u) => <UrlRow key={u.key} {...u} />)}
            </div>
            <div className="mt-4 text-[11px] text-slate-500">
              <b>Heads-up:</b> flow-locked plans (e.g. your <code className="text-white">MACK_</code> transfer pricing) only appear on the matching link above. Sending someone the general <code className="text-white">/signup/{partnerCode}</code> URL shows them a friendly picker to choose which flow they need.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UrlRow({ url, label, icon: Icon, hint, testKey }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(`${label} link copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  }
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950 p-3" data-testid={`partner-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
        <Icon className="w-4 h-4 text-cyan-400" /> {label}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
      <div className="mt-2 flex items-stretch rounded-md border border-slate-800 bg-slate-900 overflow-hidden">
        <div className="flex-1 min-w-0 px-2.5 py-1.5 font-mono text-[11px] text-cyan-200 truncate">{url}</div>
        <button type="button" onClick={copy} className="shrink-0 inline-flex items-center gap-1 px-2.5 border-l border-slate-800 hover:bg-slate-800 text-[11px] font-semibold text-slate-200">
          {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
        <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-1 px-2.5 border-l border-slate-800 hover:bg-slate-800 text-[11px] font-semibold text-slate-200">
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
